/* eslint-disable */
// Note: This is a slightly adjusted version of the original file taken from the official quickstart
// https://auth0.com/docs/quickstart/spa/react/01-login

import React, { useContext, useEffect, useState } from "react";
import { createAuth0Client } from "@auth0/auth0-spa-js";

// Strip ?code=&state= but leave navigation to React Router (see PostLoginRedirect in App.tsx).
const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

// Query params Auth0 appends to the redirect_uri. They must be removed once
// consumed (or once they turned out to be unusable) — a leftover `?code=` or
// `?error=` makes the NEXT boot re-run the callback branch against a
// transaction that is already gone, which is how the app used to get stuck.
const AUTH0_CALLBACK_PARAMS = ["code", "state", "error", "error_description"];

const stripAuth0CallbackParams = () => {
  const url = new URL(window.location.href);
  let changed = false;
  AUTH0_CALLBACK_PARAMS.forEach((param) => {
    if (url.searchParams.has(param)) {
      url.searchParams.delete(param);
      changed = true;
    }
  });
  if (!changed) return;
  window.history.replaceState(
    {},
    document.title,
    `${url.pathname}${url.search}${url.hash}`
  );
};

export let auth0Client = undefined;

export const Auth0Context = React.createContext();
export const useAuth0 = () => useContext(Auth0Context);
export const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState();
  const [user, setUser] = useState();
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  // Set when the sign-in round trip itself failed. It is what tells the app
  // NOT to bounce straight back into Auth0 (that produced an endless
  // white-screen redirect loop) and to offer a manual retry instead.
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const initAuth0 = async () => {
      try {
        const auth0FromHook = await createAuth0Client(initOptions);
        auth0Client = auth0FromHook;

        const params = new URLSearchParams(window.location.search);

        if (params.has("error")) {
          // Auth0 rejected the authorize request (consent, rule/action
          // failure, expired SSO session with prompt=none, …). It answers
          // with ?error=&state= and NO ?code=, so there is nothing to
          // exchange. Record it and clean the URL — re-entering
          // loginWithRedirect here just bounces back to the same error.
          setAuthError(params.get("error_description") || params.get("error"));
          stripAuth0CallbackParams();
        } else if (params.has("code") && params.has("state")) {
          try {
            const { appState } = await auth0FromHook.handleRedirectCallback();
            onRedirectCallback(appState);
          } catch (error) {
            // The PKCE transaction lives in sessionStorage (per tab), so it
            // is genuinely absent when the callback URL is reopened in
            // another tab, restored by the browser, or overwritten by a
            // second loginWithRedirect. This used to reject the whole init,
            // leaving `loading` true forever with no way out but restarting
            // the app.
            console.error("Auth0 redirect callback failed", error);
            setAuthError(error?.message || "redirect_callback_failed");
            stripAuth0CallbackParams();
          }
        }

        const authenticated = await auth0FromHook.isAuthenticated();
        setIsAuthenticated(authenticated);

        if (authenticated) {
          setAuthError(null);
          const user = await auth0FromHook.getUser();
          setUser(user);
        }
      } catch (error) {
        // createAuth0Client / isAuthenticated failing must still release the
        // loading gate — the app then renders the recoverable sign-in screen
        // instead of an indefinite spinner.
        console.error("Auth0 initialization failed", error);
        setAuthError(error?.message || "auth0_init_failed");
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    initAuth0();
    // eslint-disable-next-line
  }, []);

  const loginWithPopup = async (params = {}) => {
    setPopupOpen(true);
    try {
      await auth0Client.loginWithPopup(params);
    } catch (error) {
      console.error(error);
    } finally {
      setPopupOpen(false);
    }
    const user = await auth0Client.getUser();
    setUser(user);
    setIsAuthenticated(true);
  };

  const handleRedirectCallback = async () => {
    setLoading(true);
    await auth0Client.handleRedirectCallback();
    const user = await auth0Client.getUser();
    setLoading(false);
    setIsAuthenticated(true);
    setUser(user);
  };
  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        popupOpen,
        authError,
        loginWithPopup,
        handleRedirectCallback,
        // Manual, user-triggered retry after a failed sign-in round trip.
        // Clears the error first so the automatic redirect gate in App.tsx
        // is armed again; loginWithRedirect writes a fresh PKCE transaction,
        // replacing whichever stale one caused the failure.
        retryLogin: async () => {
          setAuthError(null);
          try {
            await auth0Client.loginWithRedirect({});
          } catch (error) {
            console.error("Auth0 login redirect failed", error);
            setAuthError(error?.message || "login_redirect_failed");
          }
        },
        getIdTokenClaims: (...p) => auth0Client.getIdTokenClaims(...p),
        loginWithRedirect: (...p) => auth0Client.loginWithRedirect(...p),
        getTokenSilently: (...p) => auth0Client.getTokenSilently(...p),
        getTokenWithPopup: (...p) => auth0Client.getTokenWithPopup(...p),
        logout: (...p) => {
            auth0Client.logout(...p);
            localStorage.clear();
        },
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
